import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Bell,
  CheckCircle2,
  ClipboardCheck,
  FileEdit,
  FileWarning,
  FolderKanban,
  LayoutDashboard,
  MailWarning,
  Receipt,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { GenericView, SearchInput } from '../../components/common/table';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import { fetchSalesProjectMonitoring, isDummyMonitoringData } from './salesProjectMonitoringApi';
import { formatDateDDMMYYYY } from '../../utils/formatters';

const OFFCANVAS_ID = 'offcanvasSalesProjectMonitoring';
const PAGE_SIZE_DEFAULT = 10;

const TEAL = '#1f7a82';
const INK = '#1f2d3d';
const MUTED = '#71819a';

const HEALTH_BADGE = {
  ON_TRACK: { bg: '#e7f6ec', color: '#16a34a' },
  AT_RISK: { bg: '#fff7e6', color: '#d97706' },
  OVERDUE: { bg: '#fdecec', color: '#dc2626' },
  BLOCKED: { bg: '#f1e9fb', color: '#7c3aed' },
  COMPLETED: { bg: '#e7f1fb', color: '#2563eb' },
};

const DEADLINE_BADGE = {
  ON_SCHEDULE: { bg: '#e7f6ec', color: '#16a34a' },
  DUE_SOON: { bg: '#fff7e6', color: '#d97706' },
  OVERDUE: { bg: '#fdecec', color: '#dc2626' },
  COMPLETED: { bg: '#e7f1fb', color: '#2563eb' },
};

const CONTRACT_BADGE = {
  NEED_CONTRACT: { bg: '#fff1f2', color: '#dc2626' },
  DRAFT: { bg: '#eef2ff', color: '#2563eb' },
  SENT: { bg: '#fff7e6', color: '#d97706' },
  SIGNED: { bg: '#e7f6ec', color: '#16a34a' },
  EXPIRED: { bg: '#fdecec', color: '#dc2626' },
};

const CLIENT_TYPE_BADGE = {
  NEW: { bg: '#e7f1fb', color: '#2563eb' },
  EXISTING: { bg: '#e7f6ec', color: '#16a34a' },
  PRIORITY: { bg: '#fff7e6', color: '#d97706' },
};

const LIFECYCLE_BADGE = {
  NOT_STARTED: { bg: '#f1f3f5', color: '#64748b' },
  UPCOMING: { bg: '#eef2ff', color: '#2563eb' },
  RUNNING: { bg: '#e6f7f8', color: TEAL },
  WAITING_CLIENT: { bg: '#fff7e6', color: '#d97706' },
  COMPLETED: { bg: '#e7f6ec', color: '#16a34a' },
};

const HEALTH_LABELS = {
  ON_TRACK: 'Sesuai Rencana',
  AT_RISK: 'Butuh Follow-up',
  OVERDUE: 'Terlambat',
  BLOCKED: 'Terblokir',
  COMPLETED: 'Completed',
};

const HEALTH_COLORS = {
  'Sesuai Rencana': '#16a34a',
  'Butuh Follow-up': '#d97706',
  Terlambat: '#dc2626',
  Terblokir: '#7c3aed',
  Completed: '#2563eb',
};

const BILLING_LABELS = {
  NOT_STARTED: 'Belum Ada Invoice',
  DRAFT: 'Draft',
  SENT: 'Terkirim',
  PAID: 'Lunas',
  OVERDUE: 'Terlambat',
};

const BILLING_COLORS = {
  'Belum Ada Invoice': '#868e96',
  Draft: '#2563eb',
  Terkirim: '#d97706',
  Lunas: '#16a34a',
  Terlambat: '#dc2626',
};

const STAGE_COLORS = {
  Planning: '#868e96',
  'In Progress': '#2563eb',
  Review: '#d97706',
  Completed: '#16a34a',
  Blocked: '#dc2626',
};

const AUDIT_STAGE_BADGE = {
  'Not Started': { bg: '#f1f3f5', color: '#64748b' },
  'Application Review': { bg: '#e7f1fb', color: '#2563eb' },
  Scheduling: { bg: '#e6f7f8', color: TEAL },
  Audit: { bg: '#eef2ff', color: '#4f46e5' },
  'Technical Review': { bg: '#fff7e6', color: '#d97706' },
  Certificate: { bg: '#f3e8ff', color: '#7c3aed' },
  'Application Review Rejected': { bg: '#fff1f2', color: '#dc2626' },
  Blocked: { bg: '#fdecec', color: '#dc2626' },
  Completed: { bg: '#e7f6ec', color: '#16a34a' },
};

const AUDIT_STAGE_COLORS = {
  'Not Started': '#64748b',
  'Application Review': '#2563eb',
  Scheduling: TEAL,
  Audit: '#4f46e5',
  'Technical Review': '#d97706',
  Certificate: '#7c3aed',
  'Application Review Rejected': '#dc2626',
  Blocked: '#dc2626',
  Completed: '#16a34a',
};

const actionTone = {
  NEED_CONTRACT: { color: '#dc2626', bg: '#fff1f2', icon: FileWarning },
  REVIEW_REJECTED: { color: '#c2410c', bg: '#fff7ed', icon: MailWarning },
  READY_TO_INVOICE: { color: '#7c3aed', bg: '#f3e8ff', icon: Receipt },
  CLIENT_FOLLOW_UP: { color: '#d97706', bg: '#fff7e6', icon: Bell },
  DELIVERY_AT_RISK: { color: '#dc2626', bg: '#fdecec', icon: ShieldAlert },
  ADDENDUM_PENDING: { color: '#c2410c', bg: '#fff7ed', icon: FileEdit },
};

const emptyFilters = () => ({
  search: '',
  salesOwnerId: '',
  companyId: '',
  departmentId: '',
  clientType: '',
  lifecycle: '',
  service: '',
  projectStatus: '',
  health: '',
  auditStage: '',
  invoiceStatus: '',
  contractStatus: '',
  dateFrom: '',
  dateTo: '',
  hasAddendum: '',
  hasRejectedReview: '',
  needContract: '',
  overdueOnly: '',
});

const numberValue = (value) => Number(value || 0);

const formatCount = (value) => numberValue(value).toLocaleString('id-ID');

const formatCurrency = (value) => {
  const amount = numberValue(value);
  if (!amount) return 'Rp0';
  if (amount >= 1000000000) return `Rp${(amount / 1000000000).toFixed(1)}M`;
  if (amount >= 1000000) return `Rp${(amount / 1000000).toFixed(1)}Jt`;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const relabelBreakdown = (raw, labelMap) => {
  const out = {};
  Object.entries(raw || {}).forEach(([key, value]) => {
    out[labelMap[key] || key] = value;
  });
  return out;
};

const Badge = ({ label, tone }) => (
  <span
    className="badge rounded-pill"
    style={{
      background: tone?.bg || '#f1f3f5',
      color: tone?.color || '#495057',
      fontWeight: 700,
      fontSize: '0.72rem',
      padding: '5px 10px',
      whiteSpace: 'nowrap',
    }}
  >
    {label || '-'}
  </span>
);

const Skeleton = ({ w = 'col-6', h = 20 }) => (
  <span className={`placeholder d-block rounded ${w}`} style={{ height: h }} />
);

const SummaryCard = ({ label, value, icon: Icon, color, bg, loading, caption, onClick }) => (
  <button
    type="button"
    className="card h-100 border-0 shadow-sm text-start w-100"
    onClick={onClick}
    style={{
      borderTop: `3px solid ${color}`,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform .16s ease, box-shadow .16s ease',
    }}
  >
    <div className="card-body d-flex align-items-center gap-3 py-3">
      <div
        className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: 44, height: 44, background: bg }}
      >
        <Icon size={20} color={color} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="text-muted text-truncate" style={{ fontSize: '0.71rem' }}>{label}</div>
        {loading ? (
          <div className="placeholder-glow mt-1"><Skeleton w="col-6" h={22} /></div>
        ) : (
          <div className="fw-bold mt-1" style={{ fontSize: '1.35rem', lineHeight: 1, color }}>
            {formatCount(value)}
          </div>
        )}
        {caption && <div className="small text-muted mt-1 text-truncate">{caption}</div>}
      </div>
    </div>
  </button>
);

const BreakdownList = ({ title, subtitle, data, colors, loading }) => {
  const entries = Object.entries(data || {});
  const max = Math.max(...entries.map(([, v]) => numberValue(v)), 1);
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="fw-semibold" style={{ color: INK }}>{title}</div>
        {subtitle && <div className="text-muted small mb-3">{subtitle}</div>}
        {loading ? (
          <div className="d-flex flex-column gap-2">
            {[1, 2, 3].map((i) => <div key={i} className="placeholder-glow"><Skeleton w="col-12" h={30} /></div>)}
          </div>
        ) : entries.length === 0 ? (
          <div className="text-muted small py-3 text-center">Belum ada data</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {entries.map(([key, value]) => {
              const color = colors[key] || '#868e96';
              const pct = Math.round((numberValue(value) / max) * 100);
              return (
                <div key={key}>
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="small fw-medium">{key}</span>
                    <span className="small fw-bold" style={{ color }}>{formatCount(value)}</span>
                  </div>
                  <div className="progress" style={{ height: 7, borderRadius: 4, background: 'rgba(0,0,0,.05)' }}>
                    <div className="progress-bar" style={{ width: `${pct}%`, background: color, borderRadius: 4 }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const ActionCard = ({ action, loading, onClick }) => {
  const tone = actionTone[action?.code] || { color: TEAL, bg: 'rgba(31,122,130,.1)', icon: Bell };
  const Icon = tone.icon;
  return (
    <button
      type="button"
      className="card border-0 shadow-sm text-start h-100 w-100"
      onClick={onClick}
      disabled={loading}
      style={{ cursor: loading ? 'default' : 'pointer' }}
    >
      <div className="card-body p-3">
        <div className="d-flex justify-content-between gap-2">
          <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 38, height: 38, background: tone.bg }}>
            <Icon size={18} color={tone.color} />
          </div>
          <div className="fw-bold" style={{ color: tone.color, fontSize: '1.25rem' }}>
            {loading ? '-' : formatCount(action?.count)}
          </div>
        </div>
        <div className="fw-semibold mt-3" style={{ color: INK }}>{action?.title || 'Follow-up'}</div>
        <div className="text-muted small mt-1" style={{ minHeight: 34 }}>{action?.description || 'Data belum tersedia.'}</div>
      </div>
    </button>
  );
};

const ClientJourney = ({ data, loading, onSelect }) => {
  const stages = [
    { key: 'NOT_STARTED', label: 'Not started', color: '#64748b' },
    { key: 'UPCOMING', label: 'Upcoming', color: '#2563eb' },
    { key: 'RUNNING', label: 'In progress', color: TEAL },
    { key: 'WAITING_CLIENT', label: 'Waiting for client', color: '#d97706' },
    { key: 'COMPLETED', label: 'Completed', color: '#16a34a' },
  ];
  const total = stages.reduce((sum, stage) => sum + numberValue(data?.[stage.key]), 0);
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="d-flex justify-content-between gap-3 mb-3">
          <div>
            <div className="fw-semibold" style={{ color: INK }}>Client Journey</div>
            <div className="text-muted small">Status client berdasarkan posisi project aktif.</div>
          </div>
          <Badge label={`${formatCount(total)} client`} tone={{ bg: '#e6f7f8', color: TEAL }} />
        </div>
        <div className="row g-2">
          {stages.map((stage) => {
            const value = numberValue(data?.[stage.key]);
            return (
              <div className="col-6 col-xl" key={stage.key}>
                <button
                  type="button"
                  className="w-100 text-start rounded-3 border-0 p-3"
                  onClick={() => onSelect(stage.key)}
                  style={{ background: '#f8fafc', cursor: 'pointer' }}
                >
                  <div className="small text-muted">{stage.label}</div>
                  <div className="fw-bold mt-1" style={{ color: stage.color, fontSize: '1.35rem' }}>
                    {loading ? '-' : formatCount(value)}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const ClientMatrix = ({ rows, loading, onSelect }) => {
  const columns = [
    { key: 'newClient', label: 'New', filter: 'NEW' },
    { key: 'existingClient', label: 'Existing', filter: 'EXISTING' },
    { key: 'priorityClient', label: 'Priority', filter: 'PRIORITY' },
  ];
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="fw-semibold" style={{ color: INK }}>Project Matrix by Department</div>
        <div className="text-muted small mb-3">Ringkasan client New, Existing, dan Priority per department.</div>
        {loading ? (
          <div className="placeholder-glow"><Skeleton w="col-12" h={180} /></div>
        ) : (rows || []).length === 0 ? (
          <div className="text-muted small text-center py-4">Belum ada data matrix.</div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle mb-0" style={{ minWidth: 680 }}>
              <thead className="table-light">
                <tr>
                  <th>Department</th>
                  {columns.map((col) => <th key={col.key}>{col.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.departmentId || row.departmentName}>
                    <td>
                      <div className="fw-semibold">{row.departmentName}</div>
                      <div className="text-muted small">{formatCount(row.totalProject)} project</div>
                    </td>
                    {columns.map((col) => {
                      const item = row[col.key] || {};
                      return (
                        <td key={col.key}>
                          <button
                            type="button"
                            className="border-0 rounded-3 w-100 text-start p-2"
                            onClick={() => onSelect(row.departmentId, col.filter)}
                            style={{ background: '#f8fafc', cursor: 'pointer' }}
                          >
                            <div className="fw-bold" style={{ color: CLIENT_TYPE_BADGE[col.filter]?.color }}>{formatCount(item.total)}</div>
                            <div className="small text-muted">
                              {formatCount(item.active || 0)} aktif, {formatCount(item.needContract || 0)} butuh kontrak
                            </div>
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Watchlist = ({ title, subtitle, items, empty, icon: Icon, tone, onClick }) => (
  <div className="card border-0 shadow-sm h-100">
    <div className="card-body">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <div className="d-flex align-items-center gap-2">
            <Icon size={16} color={tone} />
            <div className="fw-semibold" style={{ color: INK }}>{title}</div>
          </div>
          <div className="text-muted small mt-1">{subtitle}</div>
        </div>
        <Badge label={`${formatCount(items?.length || 0)} item`} tone={{ bg: '#f8fafc', color: tone }} />
      </div>
      {(items || []).length === 0 ? (
        <div className="text-muted small text-center py-4">{empty}</div>
      ) : (
        <div className="d-flex flex-column gap-2" style={{ maxHeight: 360, overflowY: 'auto' }}>
          {items.map((item, index) => (
            <button
              type="button"
              key={`${item.projectId || item.id}-${index}`}
              onClick={() => onClick(item)}
              className="text-start rounded-3 border-0 p-3"
              style={{ background: '#f8fafc', borderLeft: `3px solid ${tone}`, cursor: 'pointer' }}
            >
              <div className="d-flex justify-content-between gap-2">
                <span className="fw-semibold small">{item.projectCode}</span>
                <span className="small fw-semibold" style={{ color: tone }}>{item.reasonLabel || item.contractStatusLabel || item.statusLabel}</span>
              </div>
              <div className="small fw-medium mt-1">{item.projectName}</div>
              <div className="small text-muted">{item.companyName || item.clientName}</div>
              <div className="small text-muted mt-1">{item.detail || item.nextAction || item.reason}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  </div>
);

const SalesProjectMonitoring = () => {
  const navigate = useNavigate();

  const [appliedFilters, setAppliedFilters] = useState(emptyFilters());
  const [draftFilters, setDraftFilters] = useState(emptyFilters());
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingDummyData, setUsingDummyData] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSalesProjectMonitoring(
        { ...appliedFilters, search: searchTerm },
        page,
        pageSize,
      );
      setData(res);
      setUsingDummyData(isDummyMonitoringData(res));
    } catch (err) {
      setError(err?.response?.data?.message || 'Gagal memuat data Sales Project Monitoring.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, searchTerm, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const salesContext = data?.salesContext || {};
  const portfolio = data?.portfolio || {};
  const rows = data?.projects?.content || [];
  const totalRows = data?.projects?.totalElements || 0;
  const attention = data?.attentionNeeded || [];
  const contractWatchlist = data?.contractWatchlist || [];
  const reviewRejected = data?.reviewRejected || [];
  const priorityActions = useMemo(() => {
    const apiActions = data?.priorityActions;
    if (Array.isArray(apiActions) && apiActions.length) return apiActions;

    return [
      {
        code: "NEED_CONTRACT",
        title: "Need contract",
        description: "Client/project belum memiliki kontrak aktif.",
        count: summary.needContract || 0,
        tone: "warning",
        filter: { contractStatus: "Need Contract", needContract: true },
      },
      {
        code: "REJECTED_REVIEW",
        title: "Review rejected",
        description: "Application review ditolak dan perlu ditindaklanjuti sales.",
        count: summary.rejectedReview || 0,
        tone: "danger",
        filter: { hasRejectedReview: true },
      },
      {
        code: "READY_TO_INVOICE",
        title: "Ready to invoice",
        description: "Project siap masuk proses penagihan.",
        count: summary.readyToInvoice || 0,
        tone: "success",
        filter: { invoiceStatus: "Ready to Invoice" },
      },
      {
        code: "DELIVERY_RISK",
        title: "Delivery risk",
        description: "Project butuh follow-up karena risiko atau keterlambatan.",
        count: (summary.atRisk || 0) + (summary.overdue || 0),
        tone: "orange",
        filter: { health: "At Risk" },
      },
    ];
  }, [
    data?.priorityActions,
    summary.atRisk,
    summary.needContract,
    summary.overdue,
    summary.readyToInvoice,
    summary.rejectedReview,
  ]);
  const filterOptions = data?.filterOptions || {};

  const applyQuickFilter = (nextFilters) => {
    setDraftFilters((prev) => ({ ...prev, ...nextFilters }));
    setAppliedFilters((prev) => ({ ...prev, ...nextFilters }));
    setPage(0);
  };

  const handleSearchSubmit = () => {
    setPage(0);
    load();
  };

  const handleFilterApply = () => {
    setAppliedFilters(draftFilters);
    setPage(0);
  };

  const handleFilterReset = () => {
    const empty = emptyFilters();
    setDraftFilters(empty);
    setAppliedFilters(empty);
    setSearchTerm('');
    setPage(0);
  };

  const activeFilterCount = useMemo(
    () => Object.values(appliedFilters).filter((v) => v !== '' && v != null).length,
    [appliedFilters],
  );

  const actionFilters = {
    NEED_CONTRACT: { needContract: 'true' },
    REVIEW_REJECTED: { hasRejectedReview: 'true', auditStage: 'Application Review Rejected' },
    READY_TO_INVOICE: { invoiceStatus: 'NOT_STARTED', auditStage: 'Completed' },
    CLIENT_FOLLOW_UP: { lifecycle: 'WAITING_CLIENT' },
    DELIVERY_AT_RISK: { health: 'AT_RISK' },
    ADDENDUM_PENDING: { hasAddendum: 'true' },
  };

  const filterFields = [
    { key: 'salesOwnerId', label: 'Sales Owner', type: 'select', options: filterOptions.salesOwners || [] },
    { key: 'companyId', label: 'Client', type: 'select', options: filterOptions.clients || [] },
    { key: 'departmentId', label: 'Department', type: 'select', options: filterOptions.departments || [] },
    { key: 'clientType', label: 'Client Type', type: 'select', options: filterOptions.clientTypes || [] },
    { key: 'lifecycle', label: 'Client Journey', type: 'select', options: filterOptions.lifecycleStatuses || [] },
    { key: 'service', label: 'Service', type: 'select', options: filterOptions.services || [] },
    { key: 'projectStatus', label: 'Status Project', type: 'select', options: filterOptions.projectStatuses || [] },
    { key: 'auditStage', label: 'Audit Stage', type: 'select', options: filterOptions.auditStages || [] },
    { key: 'health', label: 'Health', type: 'select', options: filterOptions.healths || [] },
    { key: 'invoiceStatus', label: 'Status Invoice', type: 'select', options: filterOptions.invoiceStatuses || [] },
    { key: 'contractStatus', label: 'Status Kontrak', type: 'select', options: filterOptions.contractStatuses || [] },
    { key: 'startDate', label: 'Tanggal Mulai', type: 'dateRange', fromKey: 'dateFrom', toKey: 'dateTo' },
    {
      key: 'needContract',
      label: 'Butuh Kontrak?',
      type: 'select',
      options: [{ value: 'true', label: 'Ya' }, { value: 'false', label: 'Tidak' }],
    },
    {
      key: 'hasRejectedReview',
      label: 'Review Rejected?',
      type: 'select',
      options: [{ value: 'true', label: 'Ada' }, { value: 'false', label: 'Tidak Ada' }],
    },
    {
      key: 'hasAddendum',
      label: 'Ada Addendum?',
      type: 'select',
      options: [{ value: 'true', label: 'Ya' }, { value: 'false', label: 'Tidak' }],
    },
    {
      key: 'overdueOnly',
      label: 'Hanya Terlambat',
      type: 'select',
      options: [{ value: 'true', label: 'Ya' }],
    },
  ];

  const columns = [
    {
      key: 'companyName',
      label: 'Client',
      render: (v, row) => (
        <div style={{ minWidth: 220 }}>
          <div className="fw-semibold" style={{ color: TEAL }}>{v}</div>
          <div className="d-flex flex-wrap gap-1 mt-1">
            <Badge label={row.clientTypeLabel || row.clientType || '-'} tone={CLIENT_TYPE_BADGE[row.clientType]} />
            {row.needContract && <Badge label="Need Contract" tone={CONTRACT_BADGE.NEED_CONTRACT} />}
          </div>
        </div>
      ),
    },
    {
      key: 'projectCode',
      label: 'Project',
      render: (v, row) => (
        <div style={{ minWidth: 230 }}>
          <div className="fw-semibold">{v}</div>
          <div className="text-muted small">{row.projectName}</div>
          <div className="text-muted small">{row.serviceNames || '-'}</div>
        </div>
      ),
    },
    { key: 'departmentName', label: 'Department', render: (v) => v || '-' },
    { key: 'salesOwner', label: 'Sales Owner' },
    { key: 'deliveryPic', label: 'Delivery PIC' },
    {
      key: 'auditStage',
      label: 'Audit Stage',
      render: (v, row) => <Badge label={row.auditStageLabel || v || '-'} tone={AUDIT_STAGE_BADGE[v]} />,
    },
    {
      key: 'lifecycle',
      label: 'Journey',
      render: (v, row) => <Badge label={row.lifecycleLabel || v} tone={LIFECYCLE_BADGE[v]} />,
    },
    {
      key: 'contractStatus',
      label: 'Contract',
      render: (v, row) => <Badge label={row.contractStatusLabel || v || '-'} tone={CONTRACT_BADGE[v]} />,
    },
    {
      key: 'invoiceStatus',
      label: 'Invoice',
      render: (v, row) => <span className="small fw-medium">{row.invoiceStatusLabel || v || '-'}</span>,
    },
    {
      key: 'health',
      label: 'Health',
      render: (v, row) => <Badge label={row.healthLabel || v} tone={HEALTH_BADGE[v]} />,
    },
    {
      key: 'progressPercent',
      label: 'Progress',
      render: (v) => (
        <div style={{ minWidth: 110 }}>
          <div className="progress" style={{ height: 7, borderRadius: 4, background: '#e8edf3' }}>
            <div className="progress-bar" style={{ width: `${v || 0}%`, borderRadius: 4, background: TEAL }} />
          </div>
          <div className="small text-muted mt-1">{v || 0}%</div>
        </div>
      ),
    },
    { key: 'targetEndDate', label: 'Target', render: (v) => (v ? formatDateDDMMYYYY(v) : '-') },
    {
      key: 'deadlineStatus',
      label: 'Deadline',
      render: (v, row) => <Badge label={row.deadlineStatusLabel || v} tone={DEADLINE_BADGE[v]} />,
    },
    { key: 'nextAction', label: 'Next Action', render: (v) => <span className="small text-muted">{v || '-'}</span> },
  ];

  const handleRowClick = (row) => navigate(`/projects/detail/${row.id}`);

  return (
    <div className="container-fluid py-3" style={{ color: INK }}>
      <style>{`
        .sales-command-card:hover { transform: translateY(-1px); box-shadow: 0 .5rem 1.2rem rgba(15, 23, 42, .08) !important; }
        .sales-monitoring-table .table-responsive { min-height: 420px; }
        .sales-monitoring-table table { min-width: 1260px; }
        .sales-modern-panel { border: 1px solid #e7edf3; border-radius: 8px; background: #fff; }
        .sales-muted { color: ${MUTED}; }
        @media (max-width: 991.98px) {
          .sales-command-header { align-items: stretch !important; }
          .sales-command-header .btn { width: 100%; justify-content: center; }
        }
      `}</style>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 sales-command-header">
        <div className="d-flex align-items-start gap-3">
          <div
            className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 46, height: 46, background: 'rgba(31,122,130,0.10)' }}
          >
            <LayoutDashboard size={21} color={TEAL} />
          </div>
          <div>
            <div className="text-uppercase fw-bold sales-muted" style={{ fontSize: '.72rem', letterSpacing: 1.2 }}>
              Audit Platform
            </div>
            <h1 className="h4 mb-1 fw-bold">Sales Project Command Center</h1>
            <div className="sales-muted small">
              Pantau client, kontrak, project berjalan, billing, dan review yang perlu segera di-follow-up oleh Sales.
            </div>
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {salesContext.salesOwnerName && (
            <div className="sales-modern-panel px-3 py-2">
              <div className="text-muted small">Current portfolio</div>
              <div className="fw-semibold">{salesContext.salesOwnerName}</div>
            </div>
          )}
          <button type="button" className="btn btn-outline-secondary d-flex align-items-center gap-2" onClick={load}>
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </div>

      {usingDummyData && (
        <div className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-3">
          <AlertTriangle size={16} />
          <span className="small">Menampilkan data contoh. Endpoint monitoring belum tersedia di environment ini.</span>
        </div>
      )}

      {error && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between py-2 mb-3">
          <span className="small">{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={load}>Coba Lagi</button>
        </div>
      )}

      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3">
          <SummaryCard label="Total Project" value={summary.totalProject} icon={FolderKanban} color={TEAL} bg="rgba(31,122,130,0.10)" loading={loading} />
        </div>
        <div className="col-6 col-lg-3">
          <SummaryCard label="Application Review" value={summary.applicationReview} icon={Activity} color="#2563eb" bg="rgba(37,99,235,0.10)" loading={loading} onClick={() => applyQuickFilter({ auditStage: 'Application Review' })} />
        </div>
        <div className="col-6 col-lg-3">
          <SummaryCard label="Client Need Contract" value={summary.needContract} icon={FileWarning} color="#dc2626" bg="#fff1f2" loading={loading} onClick={() => applyQuickFilter({ needContract: 'true' })} />
        </div>
        <div className="col-6 col-lg-3">
          <SummaryCard label="Review Rejected" value={summary.rejectedReview} icon={MailWarning} color="#c2410c" bg="#fff7ed" loading={loading} onClick={() => applyQuickFilter({ hasRejectedReview: 'true' })} />
        </div>
        <div className="col-6 col-lg-3">
          <SummaryCard label="On Track" value={summary.onTrack} icon={CheckCircle2} color="#16a34a" bg="rgba(22,163,74,0.10)" loading={loading} onClick={() => applyQuickFilter({ health: 'ON_TRACK' })} />
        </div>
        <div className="col-6 col-lg-3">
          <SummaryCard label="At Risk" value={summary.atRisk} icon={AlertTriangle} color="#d97706" bg="rgba(217,119,6,0.10)" loading={loading} onClick={() => applyQuickFilter({ health: 'AT_RISK' })} />
        </div>
        <div className="col-6 col-lg-3">
          <SummaryCard label="Audit Completed" value={summary.auditCompleted ?? summary.completed} icon={CheckCircle2} color="#16a34a" bg="rgba(22,163,74,0.10)" loading={loading} onClick={() => applyQuickFilter({ auditStage: 'Completed' })} />
        </div>
        <div className="col-6 col-lg-3">
          <SummaryCard label="Ready to Invoice" value={summary.readyToInvoice} icon={Receipt} color="#7c3aed" bg="rgba(124,58,237,0.10)" loading={loading} onClick={() => applyQuickFilter({ invoiceStatus: 'NOT_STARTED', auditStage: 'Completed' })} />
        </div>
      </div>

      <div className="row g-3 mb-3">
        {(loading ? [1, 2, 3, 4, 5, 6] : priorityActions).slice(0, 6).map((action, index) => (
          <div className="col-12 col-md-6 col-xl-2" key={action?.code || index}>
            <ActionCard
              action={action}
              loading={loading}
              onClick={() => applyQuickFilter(actionFilters[action?.code] || {})}
            />
          </div>
        ))}
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-7">
          <ClientJourney
            data={portfolio.clientJourney}
            loading={loading}
            onSelect={(lifecycle) => applyQuickFilter({ lifecycle })}
          />
        </div>
        <div className="col-12 col-xl-5">
          <BreakdownList
            title="Contract and Billing"
            subtitle="Kontrak dan status penagihan yang perlu dipantau."
            data={relabelBreakdown(portfolio.billing, BILLING_LABELS)}
            colors={BILLING_COLORS}
            loading={loading}
          />
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-7">
          <ClientMatrix
            rows={portfolio.clientMatrix}
            loading={loading}
            onSelect={(departmentId, clientType) => applyQuickFilter({ departmentId, clientType })}
          />
        </div>
        <div className="col-12 col-xl-5">
          <div className="row g-3">
            <div className="col-12">
              <BreakdownList
                title="Project Health"
                subtitle="Kesehatan delivery semua project Sales."
                data={relabelBreakdown(portfolio.health, HEALTH_LABELS)}
                colors={HEALTH_COLORS}
                loading={loading}
              />
            </div>
            <div className="col-12">
              <BreakdownList
                title="Audit Platform Stage"
                subtitle="Current audit stage from Audit Platform tasks."
                data={portfolio.auditStage || portfolio.deliveryStage}
                colors={portfolio.auditStage ? AUDIT_STAGE_COLORS : STAGE_COLORS}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-6">
          <Watchlist
            title="Contract Watchlist"
            subtitle="Client/project yang butuh kontrak, PO, atau kelengkapan komersial."
            items={contractWatchlist}
            empty="No contracts require follow-up."
            icon={ClipboardCheck}
            tone="#dc2626"
            onClick={(item) => navigate(`/projects/detail/${item.projectId}`)}
          />
        </div>
        <div className="col-12 col-xl-6">
          <Watchlist
            title="Application Review Rejected"
            subtitle="Rejected review harus terlihat oleh Sales sampai diterima dan ditindaklanjuti."
            items={reviewRejected}
            empty="No rejected application reviews."
            icon={MailWarning}
            tone="#c2410c"
            onClick={(item) => navigate(`/projects/detail/${item.projectId}`)}
          />
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-xxl-9">
          <div className="card border-0 shadow-sm sales-monitoring-table">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
                <div>
                  <div className="fw-semibold" style={{ color: INK }}>Project Monitoring</div>
                  <div className="text-muted small">Klik baris untuk masuk ke Project Detail.</div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Badge label={`${formatCount(totalRows)} records`} tone={{ bg: '#e6f7f8', color: TEAL }} />
                  {activeFilterCount > 0 && <Badge label={`${activeFilterCount} filter aktif`} tone={{ bg: '#fff7e6', color: '#d97706' }} />}
                </div>
              </div>
              <GenericView
                data={rows}
                columns={columns}
                checkboxSelection={false}
                isLoading={loading}
                onRowClick={handleRowClick}
                emptyStateMessage="Belum ada project yang cocok dengan filter ini."
                tableClassName="table card-table table-vcenter text-nowrap table-hover"
                uniqueId="sales-project-monitoring"
                showColumnToggle
                filterComponent={<FilterButton offcanvasId={OFFCANVAS_ID} activeCount={activeFilterCount} label="Filter" />}
                searchComponent={
                  <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    onSearchSubmit={handleSearchSubmit}
                    placeholder="Cari project code / nama project / client..."
                    uniqueId="sales-project-monitoring-search"
                  />
                }
                pagination={{
                  currentPage: page + 1,
                  totalRows,
                  rowsPerPage: pageSize,
                  onPageChange: (p) => setPage(p - 1),
                  onRowsPerPageChange: (s) => { setPageSize(s); setPage(0); },
                }}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-xxl-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-3">
                <Bell size={16} color="#c2410c" />
                <div className="fw-semibold" style={{ color: INK }}>Need Sales Attention</div>
              </div>
              {loading ? (
                <div className="d-flex flex-column gap-2">
                  {[1, 2, 3].map((i) => <div key={i} className="placeholder-glow"><Skeleton w="col-12" h={54} /></div>)}
                </div>
              ) : attention.length === 0 ? (
                <div className="text-muted small text-center py-4">No projects need attention right now.</div>
              ) : (
                <div className="d-flex flex-column gap-2" style={{ maxHeight: 620, overflowY: 'auto' }}>
                  {attention.map((item, idx) => (
                    <button
                      key={`${item.projectId}-${item.reasonCode}-${idx}`}
                      type="button"
                      onClick={() => navigate(`/projects/detail/${item.projectId}`)}
                      className="text-start p-3 rounded-3 border-0"
                      style={{ background: '#f8fafc', cursor: 'pointer', borderLeft: '3px solid #c2410c' }}
                    >
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <span className="fw-semibold small">{item.projectCode}</span>
                        <span className="badge rounded-pill" style={{ background: '#fdecec', color: '#dc2626', fontSize: '0.68rem' }}>
                          {item.reasonLabel}
                        </span>
                      </div>
                      <div className="small text-truncate mt-1" style={{ color: '#495057' }}>{item.projectName}</div>
                      <div className="small text-muted text-truncate">{item.companyName}</div>
                      <div className="small text-muted">{item.detail}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AdvancedFilterPanel
        offcanvasId={OFFCANVAS_ID}
        title="Filter Sales Project Monitoring"
        showPeriod={false}
        filterData={draftFilters}
        onChange={(key, val) => setDraftFilters((prev) => ({ ...prev, [key]: val }))}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={filterFields}
      />
    </div>
  );
};

export default SalesProjectMonitoring;
